import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ViewContainerRef,
  booleanAttribute,
  computed,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs';
import { HlmBadge } from '@lite-workspace/shared/helm-ui/badge';
import { ConfirmDialogComponent, HlmDialogOpener, type ConfirmDialogContext } from '@lite-workspace/shared/helm-ui/dialog';
import { HlmDropdownMenuImports, HlmDropdownMenuTrigger } from '@lite-workspace/shared/helm-ui/dropdown-menu';
import { IconComponent } from '@lite-workspace/shared/blocks/icon';
import { IdentityStore } from '@lite-workspace/shared/identity';
import { SUPPORT_CHANNEL_MENU } from '@lite-workspace/shared/onboarding-service';
import { AvatarComponent } from '@lite-workspace/shared/blocks/avatar';
import type { AccountMenuItem } from './account-menu.model';

/**
 * The one profile menu for every LUMIR product: identity avatar trigger, name + email header,
 * app-supplied items, and a built-in confirmed logout. Everything product-specific arrives from
 * the outside — `items` for rows, the `accountMenuHeader` / `accountMenuFooter` slots for extras
 * (a balance pill, a theme toggle) — so the menu itself depends on no app.
 */
@Component({
  selector: 'app-account-menu',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HlmBadge, HlmDropdownMenuImports, IconComponent, AvatarComponent],
  // The host is a flex box, not the default inline one: an inline host puts its inline-block trigger on
  // a text baseline, so the host's line box carries the font's descender under the avatar (~1.7px at the
  // inherited 16/24 type) and the circle rides that half above the centre of any `items-center` bar.
  host: { class: 'flex' },
  template: `
    <button
      type="button"
      [hlmDropdownMenuTrigger]="menu"
      [align]="align()"
      side="bottom"
      aria-label="Меню профиля"
      class="relative size-8 rounded-full outline-none after:absolute after:-inset-2 after:content-['']"
      (hlmDropdownMenuOpened)="opened.emit()"
    >
      <app-avatar size="full" ring="hairline" [src]="avatarUrl()" />
    </button>

    <ng-template #menu>
      <div hlmDropdownMenu class="w-78 max-w-[var(--menu-max-w)]">
        <div class="acc-menu-head mb-1.5 grid items-center gap-x-3.25 border-b border-line px-2 pb-4 pt-2">
          <div class="row-span-2 self-center">
            <app-avatar size="11" ring="hairline" [src]="avatarUrl()" />
          </div>
          <p class="min-w-0 truncate text-sm font-semibold leading-tight text-ink">{{ displayName() }}</p>
          <ng-content select="[accountMenuHeader]" />
          <p class="col-span-2 col-start-2 row-start-2 mt-0.5 truncate text-xs text-c3">{{ email() }}</p>
        </div>

        <!-- Акцентная строка отличается только тоном: иконка рисуется по currentColor, поэтому
             один класс на кнопке красит и текст, и значок. -->
        @for (item of items(); track item.id) {
          @if (item.children; as children) {
            <!-- Открытая строка — нейтральная ступень поверхности, как в эталоне, не акцентная заливка. -->
            <button
              hlmDropdownMenuItem
              type="button"
              class="px-3 py-2.75 text-c2 aria-expanded:bg-panel3 aria-expanded:text-ink"
              [hlmDropdownMenuSubTrigger]="sub"
              (hlmDropdownMenuSubOpened)="item.action()"
            >
              @if (item.icon; as icon) {
                <app-icon [name]="icon" [size]="16" />
              }
              <span>{{ item.label }}</span>
              <app-icon name="chevron-right" [size]="16" class="ms-auto" />
            </button>
            <ng-template #sub>
              <!-- 8px воздуха до плашки меню (строка стоит на 9px внутри неё) и первая строка вровень с открывшей. -->
              <div
                hlmDropdownMenuSub
                class="-mt-2.5 data-[side=left]:mx-4.25 data-[side=right]:mx-4.25"
                [class]="channelLook.plate"
              >
                @for (child of children; track child.id) {
                  <button hlmDropdownMenuItem type="button" [class]="channelLook.row" (triggered)="child.action()">
                    @if (child.icon; as icon) {
                      <app-icon [name]="icon" [size]="16" />
                    }
                    <span>{{ child.label }}</span>
                  </button>
                  @if (child.separatorAfter) {
                    <hlm-dropdown-menu-separator />
                  }
                }
              </div>
            </ng-template>
          } @else {
            <button
              hlmDropdownMenuItem
              type="button"
              class="px-3 py-2.75"
              [class.text-c2]="!item.accent"
              [class.text-acc2]="item.accent"
              (triggered)="item.action()"
            >
              @if (item.icon; as icon) {
                <app-icon [name]="icon" [size]="16" />
              }
              <span>{{ item.label }}</span>
              <!-- Счётчик прижат к правому краю строки: та же плашка, что стояла у «Подсказок» в меню «?». -->
              @if (item.badge) {
                <span hlmBadge variant="neutral" class="ms-auto">{{ item.badge }}</span>
              }
              @if (item.trail; as trail) {
                <span class="ms-auto ps-4 text-xs text-c3 tabular-nums">{{ trail }}</span>
              }
            </button>
          }
          @if (item.separatorAfter) {
            <hlm-dropdown-menu-separator />
          }
        }

        <!-- Без пунктов разделитель встал бы вплотную к линии шапки — две волосяные линии подряд. -->
        @if (items().length > 0) {
          <hlm-dropdown-menu-separator />
        }

        <div class="flex items-center gap-2" [class.justify-end]="!showLogout()">
          @if (showLogout()) {
            <button
              hlmDropdownMenuItem
              type="button"
              variant="destructive"
              class="flex-1 px-3 py-2.75"
              (triggered)="confirmLogout()"
            >
              <app-icon name="log-out" [size]="16" />
              <span>Выйти</span>
            </button>
          }

          <ng-content select="[accountMenuFooter]" />
        </div>
      </div>
    </ng-template>
  `,
  styles: [
    `
      .acc-menu-head {
        grid-template-columns: auto minmax(0, 1fr) auto;
      }
    `,
  ],
})
export class AccountMenuComponent {
  private readonly identity = inject(IdentityStore);
  private readonly dialogs = inject(HlmDialogOpener);
  private readonly vcr = inject(ViewContainerRef);
  private readonly destroyRef = inject(DestroyRef);

  readonly items = input<readonly AccountMenuItem[]>([]);
  /** Menu edge to align with the trigger: 'end' fits a screen-right avatar, 'start' a screen-left one. */
  readonly align = input<'start' | 'end'>('end');

  /**
   * Sign-out row. A surface where the session is not the viewer's own to end — the owner previewing the
   * coordinator view — keeps the same menu without it; the way out of that mode is its own banner.
   */
  readonly showLogout = input(true, { transform: booleanAttribute });

  /**
   * Header title for an account the product names differently from the person behind it — a partner
   * cabinet leads with its studio. Empty keeps the identity's own name.
   */
  readonly nameOverride = input<string | null>(null);

  /**
   * Photo for both avatars. Identity holds no picture, so the product that HAS one (a portfolio
   * visitka) hands it over; without it the avatar wears the one system silhouette, never initials.
   */
  readonly avatarUrl = input<string | null>(null);

  /** The app owns the sign-out itself — the menu only reports that the photographer confirmed it. */
  readonly logoutConfirmed = output<void>();

  /** Fires on every panel opening — the hosts hang their analytics here. */
  readonly opened = output<void>();

  private readonly trigger = viewChild(HlmDropdownMenuTrigger);

  /**
   * Slot content is projected from the host's view, which has no MENU_STACK, so it cannot be a
   * `hlmDropdownMenuItem` and never closes the panel on its own. A host opening a dialog from a slot
   * calls this first, or the menu stays parked under the dialog scrim.
   */
  close(): void {
    this.trigger()?.close();
  }

  // The submenu holds the support channels: same plate and rows as the channel menu outside any menu.
  protected readonly channelLook = SUPPORT_CHANNEL_MENU;
  protected readonly fullName = this.identity.fullName;
  protected readonly displayName = computed(() => this.nameOverride() || this.fullName());
  protected readonly email = computed(() => this.identity.profile()?.email ?? '');

  protected confirmLogout(): void {
    const ref = this.dialogs.open<ConfirmDialogContext, boolean>(ConfirmDialogComponent, this.vcr, {
      title: 'Выйти из аккаунта?',
      message: 'Вы вернётесь на экран входа.',
      confirmLabel: 'Выйти',
      danger: true,
    });
    ref.closed$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(confirmed => {
      if (confirmed) this.logoutConfirmed.emit();
    });
  }
}
